// client/src/components/public/PublicActionCard.jsx
//
// One open (or opening-soon) raffle or auction on the tenant's public pages:
// the hub's "Raffles & auctions" section and /hub/:tenantSlug/actions. The
// CTA goes to the item's existing entry / bid page (EventAction); the event
// name links to its public event page. Data: GET /api/public/tenants/:slug/
// actions (server/services/publicActions.js). Styles: PublicHub.css (.hub-act*).

import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currencyUtils';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Absolute date/time in the tenant's time zone (falls back to the viewer's). */
export function formatWhen(iso, timeZone) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const opts = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  try {
    return d.toLocaleString(undefined, { ...opts, timeZone: timeZone || undefined });
  } catch {
    return d.toLocaleString(undefined, opts);
  }
}

/** "Closes in 3 days" / "Opens Fri, Nov 7, 6:00 PM" + whether it is closing within a day. */
function useTiming(action, now, timeZone) {
  const { t } = useTranslation();
  if (action.state === 'upcoming') {
    const when = formatWhen(action.opensAt, timeZone);
    return { text: when ? t('hub.actions.opens', 'Opens {{when}}', { when }) : t('hub.actions.openingSoon', 'Opening soon'), soon: false };
  }
  if (!action.closesAt) return { text: t('hub.actions.openNow', 'Open now'), soon: false };
  const left = Date.parse(action.closesAt) - now;
  if (left <= 0) return { text: t('hub.actions.closing', 'Closing now'), soon: true };
  if (left < HOUR) return { text: t('hub.actions.closesMinutes', 'Closes in {{count}} min', { count: Math.max(1, Math.round(left / MINUTE)) }), soon: true };
  if (left < DAY) return { text: t('hub.actions.closesHours', 'Closes in {{count}} h', { count: Math.round(left / HOUR) }), soon: true };
  const days = Math.floor(left / DAY);
  return { text: t('hub.actions.closesDays', { count: days, defaultValue: days === 1 ? 'Closes in 1 day' : 'Closes in {{count}} days' }), soon: false };
}

/** The one fact that decides whether to click: entry price, or the bid to beat. */
function useHeadline(action) {
  const { t } = useTranslation();
  if (action.kind === 'raffle') {
    const r = action.raffle || {};
    const price = r.price != null ? formatCurrency(r.price, r.currency) : null;
    if (r.entry === 'paid') return t('hub.actions.pricePerEntry', '{{price}} per entry', { price });
    if (r.entry === 'first_free') return t('hub.actions.firstFree', 'First entry free · {{price}} per extra entry', { price });
    return t('hub.actions.freeEntry', 'Free to enter');
  }
  const a = action.auction || {};
  if (a.highBid != null) {
    return t('hub.actions.currentBid', { count: a.bids, bid: formatCurrency(a.highBid, a.currency), defaultValue: a.bids === 1 ? 'Current bid {{bid}} · 1 bid' : 'Current bid {{bid}} · {{count}} bids' });
  }
  if (a.startingBid != null) return t('hub.actions.startingBid', 'Starting bid {{bid}} · no bids yet', { bid: formatCurrency(a.startingBid, a.currency) });
  return t('hub.actions.noBids', 'No bids yet');
}

export default function PublicActionCard({ action, now = Date.now(), timeZone }) {
  const { t } = useTranslation();
  const timing = useTiming(action, now, timeZone);
  const headline = useHeadline(action);
  const isRaffle = action.kind === 'raffle';
  const kindLabel = isRaffle ? t('hub.actions.raffle', 'Raffle') : t('hub.actions.auction', 'Auction');
  const cta = action.state === 'upcoming'
    ? t('hub.actions.ctaView', 'View details')
    : (isRaffle ? t('hub.actions.ctaRaffle', 'Enter raffle') : t('hub.actions.ctaAuction', 'Place a bid'));
  const closesTitle = action.state === 'open' ? formatWhen(action.closesAt, timeZone) : null;

  return (
    <article className="hub-act" aria-label={action.name}>
      {action.image
        ? <img className="hub-act-img" src={action.image} alt="" loading="lazy" />
        : <span className={`hub-act-img hub-act-ph ${action.kind}`} aria-hidden="true">{isRaffle ? '🎟' : '🔨'}</span>}
      <div className="hub-act-body">
        <div className="hub-act-tags">
          <span className={`hub-act-kind ${action.kind}`}>{kindLabel}</span>
          <span className={`hub-act-time${timing.soon ? ' soon' : ''}`} title={closesTitle ? t('hub.actions.closesAt', 'Closes {{when}}', { when: closesTitle }) : undefined}>
            {timing.text}
          </span>
        </div>
        <h3 className="hub-act-name">{action.name}</h3>
        {action.event?.name && (
          <a className="hub-act-event" href={action.event.url}>{action.event.name}</a>
        )}
        {action.description && <p className="hub-act-desc">{action.description}</p>}
        <div className="hub-act-foot">
          <span className="hub-act-headline">{headline}</span>
          <a className="hub-act-cta" href={action.url} aria-label={`${cta}: ${action.name}`}>{cta}</a>
        </div>
      </div>
    </article>
  );
}
